import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { House, ChartLineUp, Bank, Sparkle, SlidersHorizontal, SignOut, TrendUp, Star } from "@phosphor-icons/react";
import { Button } from "../components/ui/button";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: House, testId: "nav-dashboard" },
  { to: "/portfolio", label: "Portfolio", icon: ChartLineUp, testId: "nav-portfolio" },
  { to: "/watchlist", label: "Watchlist", icon: Star, testId: "nav-watchlist" },
  { to: "/banks", label: "Banks", icon: Bank, testId: "nav-banks" },
  { to: "/style", label: "Style Trainer", icon: SlidersHorizontal, testId: "nav-style" },
  { to: "/recommendations", label: "AI Advisor", icon: Sparkle, testId: "nav-recommendations" },
];

export default function AppLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();

  const doLogout = async () => {
    await logout();
    nav("/login", { replace: true });
  };

  return (
    <div className="min-h-screen flex bg-zinc-950 text-zinc-100">
      {/* Sidebar */}
      <aside className="hidden lg:flex w-64 shrink-0 flex-col border-r border-zinc-900 bg-zinc-950/80 backdrop-blur-xl">
        <div className="p-6 flex items-center gap-3">
          <div className="h-9 w-9 rounded-lg bg-emerald-600 flex items-center justify-center">
            <TrendUp size={20} weight="bold" />
          </div>
          <div>
            <div className="font-display font-semibold">Sirbansal</div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">Wealth OS</div>
          </div>
        </div>
        <nav className="flex-1 px-3 space-y-1">
          {NAV.map(({ to, label, icon: Icon, testId }) => (
            <NavLink key={to} to={to} data-testid={testId}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors ${
                  isActive
                    ? "bg-emerald-600/15 text-emerald-300 border border-emerald-700/30"
                    : "text-zinc-400 hover:bg-zinc-900 hover:text-zinc-100 border border-transparent"
                }`
              }>
              <Icon size={18} weight="regular" />
              <span>{label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-zinc-900 space-y-3">
          <div className="text-xs text-zinc-500">Signed in as</div>
          <div className="text-sm truncate" data-testid="sidebar-user-email">{user?.email}</div>
          <Button size="sm" variant="outline" onClick={doLogout} data-testid="logout-button"
            className="w-full bg-zinc-900 border-zinc-800 hover:bg-zinc-800 hover:text-zinc-100">
            <SignOut size={16} className="mr-2" /> Sign out
          </Button>
        </div>
      </aside>

      {/* Mobile top-bar */}
      <div className="lg:hidden fixed top-0 inset-x-0 z-30 border-b border-zinc-900 bg-zinc-950/90 backdrop-blur-xl">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2">
            <div className="h-7 w-7 rounded-md bg-emerald-600 flex items-center justify-center">
              <TrendUp size={14} weight="bold" />
            </div>
            <span className="font-display font-semibold">Sirbansal</span>
          </div>
          <Button size="sm" variant="ghost" onClick={doLogout} className="text-zinc-400 hover:text-zinc-100" data-testid="logout-button-mobile">
            <SignOut size={16} />
          </Button>
        </div>
        <div className="flex overflow-x-auto px-2 py-2 gap-1">
          {NAV.map(({ to, label, icon: Icon }) => (
            <NavLink key={to} to={to}
              className={({ isActive }) =>
                `shrink-0 flex items-center gap-2 px-3 py-1.5 text-xs rounded-full ${
                  isActive ? "bg-emerald-600/20 text-emerald-300" : "text-zinc-400 bg-zinc-900"
                }`
              }>
              <Icon size={14} />{label}
            </NavLink>
          ))}
        </div>
      </div>

      <main className="flex-1 lg:pl-0 pt-28 lg:pt-0 min-w-0">
        <div className="p-6 md:p-10 max-w-[1400px]">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
